import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BrandLogo from "../../components/BrandLogo";
import {
  FiTwitter,
  FiLinkedin,
  FiGithub,
  FiInstagram,
  FiMail,
  FiPhone,
  FiHeadphones,
  FiMapPin,
  FiZap,
} from "react-icons/fi";
import { publicSettingsApi } from "../../api/publicApi";

const LINKS = {
  Platform: [
    { label: "Browse Courses", path: "/courses" },
    { label: "Pricing", path: "/pricing" },
    { label: "For Providers", path: "/provider" },
    { label: "Enterprise", path: "/#" },
  ],
  Company: [
    { label: "About Us", path: "/about" },
    { label: "Sponsors", path: "/#" },
    { label: "Blog", path: "/#" },
    { label: "Contact", path: "/contact" },
  ],
  Legal: [
    { label: "Privacy Policy", path: "/privacy" },
    { label: "Terms of Service", path: "/terms" },
    { label: "Refund Policy", path: "/refund-policy" },
    { label: "Cookie Policy", path: "/cookies" },
  ],
};

const SOCIALS = [
  { icon: <FiTwitter size={16} />, href: "#", label: "Twitter" },
  { icon: <FiLinkedin size={16} />, href: "#", label: "LinkedIn" },
  { icon: <FiGithub size={16} />, href: "#", label: "GitHub" },
  { icon: <FiInstagram size={16} />, href: "#", label: "Instagram" },
];

export default function Footer() {
  const [settings, setSettings] = useState({
    support_email: "support@fullcircl.com",
    support_phone: "+1 (800) 555-0199",
    footer_copyright: "© 2026 FullCirkl. All rights reserved.",
  });

  useEffect(() => {
    publicSettingsApi()
      .then((res) => {
        if (res.data?.data) {
          setSettings((prev) => ({ ...prev, ...res.data.data }));
        }
      })
      .catch(() => {});
  }, []);

  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="border-t border-[#8A9778]/15 bg-gradient-to-br from-[#4a5243] via-[#6B7760] to-[#4a5243] text-[#F8F5F0]/80">
      {/* Main footer grid */}
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 md:grid-cols-4 lg:grid-cols-5">
          {/* Brand & Support Contacts */}
          <div className="col-span-1 sm:col-span-2 lg:col-span-2 space-y-4">
            <div onClick={handleScrollTop} className="cursor-pointer">
              <BrandLogo to="/" subtitle="" inverted size={100} />
            </div>
            <p className="max-w-xs text-sm leading-relaxed text-[#F8F5F0]/70">
              Empowering healthcare professionals and learners to discover accredited medical and dental courses worldwide.
            </p>

            {/* Direct Support Contacts */}
            <div className="space-y-2 pt-2">
              {settings.support_email && (
                <div className="flex items-center gap-2.5 text-xs text-[#F8F5F0]/80">
                  <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-white/10 text-[#D4A45A]">
                    <FiMail size={12} />
                  </div>
                  <span>Email:</span>
                  <a
                    href={`mailto:${settings.support_email}`}
                    className="font-medium text-white hover:text-[#D4A45A] transition-colors"
                  >
                    {settings.support_email}
                  </a>
                </div>
              )}

              {settings.support_phone && (
                <div className="flex items-center gap-2.5 text-xs text-[#F8F5F0]/80">
                  <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-white/10 text-[#D4A45A]">
                    <FiPhone size={12} />
                  </div>
                  <span>Helpline:</span>
                  <a
                    href={`tel:${settings.support_phone}`}
                    className="font-medium text-white hover:text-[#D4A45A] transition-colors"
                  >
                    {settings.support_phone}
                  </a>
                </div>
              )}
            </div>

            <div className="pt-2 flex items-center gap-2">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#8A9778]/25 text-[#F8F5F0]/70 no-underline transition hover:border-[#D4A45A] hover:bg-[#D4A45A]/10 hover:text-[#D4A45A]"
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(LINKS).map(([title, items]) => (
            <div key={title}>
              <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-[#D4A45A]/80">
                {title}
              </p>
              <ul className="space-y-3">
                {items.map((item) => (
                  <li key={item.label}>
                    <Link
                      to={item.path}
                      onClick={handleScrollTop}
                      className="text-sm text-[#F8F5F0]/65 no-underline transition hover:text-[#D4A45A]"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-[#8A9778]/20">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-5 text-xs text-[#F8F5F0]/60 sm:flex-row sm:px-6 lg:px-8">
          <p>{settings.footer_copyright || `© ${new Date().getFullYear()} FullCirkl. All rights reserved.`}</p>
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[#F8F5F0]/70 font-medium">All systems operational</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
